import React, { useState, useEffect } from 'react';
import './AddOrder.css';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, auth, functions } from './firebase';
import { useGlobal } from './GlobalContext';
import Navbar from './components/Navbar';
import DatePicker from 'react-multi-date-picker';
import {
  FaPlus, FaMinus, FaTrash, FaSave, FaMapMarkerAlt,
  FaCalendarAlt, FaUtensils, FaLeaf, FaBox
} from 'react-icons/fa';

// ── Helpers ─────────────────────────────────────────────────────────────────
const generateOrderId = () => `oid-${Date.now()}-${Math.random().toString(16).slice(2)}`;
const pad = (v) => String(v).padStart(2, '0');
const getAuditTimestamp = () => {
  const now = new Date();
  return {
    iso: now.toISOString(),
    date: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
    time: `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`,
  };
};
const normalizeVenue = (v) => (v || '').toString().trim().toLowerCase();
const venueKey   = (o) => `${normalizeVenue(o?.address)}|${normalizeVenue(o?.postcode)}`;
const venueLabel = (o) => {
  const a = (o?.address || '').trim();
  const p = (o?.postcode || '').trim();
  if (a && p) return `${a} (${p})`;
  return a || p || 'All Venues';
};

const blankOrder = (postcode = '', address = '') => ({
  dates: [], totalMeals: 15, specialMeals: 0,
  cutlery: false, cutleryQuantity: '',
  paperBowl: false, paperBowlQuantity: '',
  plasticContainer: false, plasticContainerQuantity: '',
  packingInstructions: '', postcode, address, isApproved: false,
});

const disableWeekends = ({ date }) => {
  const d = date.weekDay.index;
  if (d === 0 || d === 6) return { disabled: true, style: { color: '#ccc', cursor: 'not-allowed' } };
};

// ── Extra toggle item ────────────────────────────────────────────────────────
const ExtraToggle = ({ icon, label, checked, onToggle, qty, onQty, disabled }) => (
  <div className={`extra-toggle ${checked ? 'active' : ''} ${disabled ? 'disabled' : ''}`}>
    <button type="button" className="extra-toggle-main" onClick={onToggle} disabled={disabled}>
      <span className="extra-toggle-icon">{icon}</span>
      <span className="extra-toggle-label">{label}</span>
      <span className={`extra-toggle-chip ${checked ? 'on' : ''}`}>{checked ? 'On' : 'Off'}</span>
    </button>
    {checked && (
      <div className="extra-toggle-qty">
        <label>Qty / day</label>
        <input
          type="number"
          min="1"
          value={qty || ''}
          onChange={onQty}
          placeholder="0"
        />
      </div>
    )}
  </div>
);

// ── Stepper ──────────────────────────────────────────────────────────────────
const Stepper = ({ label, sublabel, value, onChange, min = 0 }) => (
  <div className="stepper">
    <div className="stepper-label">
      <span>{label}</span>
      {sublabel && <small>{sublabel}</small>}
    </div>
    <div className="stepper-controls">
      <button
        type="button"
        className="stepper-btn"
        onClick={() => onChange(Math.max(min, Number(value) - 1))}
        disabled={Number(value) <= min}
      >
        <FaMinus />
      </button>
      <input
        type="number"
        className="stepper-input"
        value={value}
        min={min}
        onChange={(e) => onChange(Math.max(min, Number(e.target.value)))}
      />
      <button
        type="button"
        className="stepper-btn"
        onClick={() => onChange(Number(value) + 1)}
      >
        <FaPlus />
      </button>
    </div>
  </div>
);

// ── Main Component ───────────────────────────────────────────────────────────
const AddOrder = () => {
  const { isBusinessInfoComplete } = useGlobal();
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [currentOrder, setCurrentOrder] = useState([]);
  const [venues, setVenues] = useState([]); // full venue objects { address, postCode }
  const [businessName, setBusinessName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isBusinessInfoComplete) { navigate('/business-info'); return; }
    const fetchOrders = async () => {
      const user = auth.currentUser;
      if (!user) return;
      try {
        const snap = await getDoc(doc(db, 'ClientContactInfo', user.uid));
        if (snap.exists()) {
          const data = snap.data();
          setOrders(data.orders || []);
          setBusinessName(data.businessName || data.contactPerson || '');
          // Keep full venue pairs so address ↔ postcode can auto-link
          const venuePairs = (data.venues || [])
            .filter(v => v.address || v.postCode)
            .map(v => ({ address: v.address || '', postCode: v.postCode || '' }));
          setVenues(venuePairs);
        }
      } catch (e) { console.error(e); }
    };
    fetchOrders();
  }, [isBusinessInfoComplete, navigate, auth, db]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleAdd = () => {
    const first = venues[0];
    setCurrentOrder(prev => [...prev, blankOrder(first?.postCode || '', first?.address || '')]);
  };

  // When postcode changes → auto-fill the paired address
  const handlePostcodeChange = (i, postcode) => {
    if (postcode === 'Pick up') {
      updateOrder(i, { postcode: 'Pick up', address: 'Pick up' });
      return;
    }
    const matched = venues.find(v => v.postCode === postcode);
    updateOrder(i, {
      postcode,
      address: matched ? matched.address : currentOrder[i].address,
    });
  };

  // When address changes → auto-fill the paired postcode
  const handleAddressChange = (i, address) => {
    if (address === 'Pick up') {
      updateOrder(i, { postcode: 'Pick up', address: 'Pick up' });
      return;
    }
    const matched = venues.find(v => v.address === address);
    updateOrder(i, {
      address,
      postcode: matched ? matched.postCode : currentOrder[i].postcode,
    });
  };

  const handleRemove = (i) => setCurrentOrder(prev => prev.filter((_, idx) => idx !== i));

  const updateOrder = (i, patch) =>
    setCurrentOrder(prev => prev.map((o, idx) => idx === i ? { ...o, ...patch } : o));

  const handleDatesChange = (i, selected) =>
    updateOrder(i, { dates: (selected || []).map(d => d.format('YYYY-MM-DD')) });

  const handleTotalMeals = (i, v) => {
    const total = Math.max(15, v);
    const special = Math.min(currentOrder[i].specialMeals, total);
    updateOrder(i, { totalMeals: total, specialMeals: special });
  };

  const handleSpecialMeals = (i, v) => {
    const max = currentOrder[i].totalMeals;
    updateOrder(i, { specialMeals: Math.min(Math.max(0, v), max) });
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    const user = auth.currentUser;
    if (!user) { alert('No user signed in.'); return; }

    const allFilled = currentOrder.every(o =>
      o.dates.length > 0 && o.postcode && o.address &&
      o.totalMeals >= 15
    );
    if (!allFilled) {
      alert('Please fill in all required fields (location, at least one date, min 15 meals).');
      return;
    }

    const duplicates = [];
    for (const o of currentOrder) {
      for (const date of o.dates) {
        if (orders.some(ex => ex.postcode === o.postcode && ex.address === o.address && ex.date === date))
          duplicates.push(date);
      }
    }
    if (duplicates.length > 0) {
      alert(`These dates already have orders for the same location: ${duplicates.join(', ')}. Go to Manage Orders to edit.`);
      return;
    }

    setSaving(true);
    try {
      const placedAt = getAuditTimestamp();
      const expanded = currentOrder.flatMap(o =>
        o.dates.map(date => ({
          dateType: 'individual', date, from: '', to: '',
          totalMeals: o.totalMeals, specialMeals: o.specialMeals,
          cutlery: o.cutlery, cutleryQuantity: o.cutleryQuantity || '',
          paperBowl: o.paperBowl, paperBowlQuantity: o.paperBowlQuantity || '',
          plasticContainer: o.plasticContainer, plasticContainerQuantity: o.plasticContainerQuantity || '',
          packingInstructions: o.packingInstructions,
          postcode: o.postcode, address: o.address,
          isApproved: false,
          orderPlacedAt: placedAt.iso, orderPlacedDate: placedAt.date, orderPlacedTime: placedAt.time,
          orderId: generateOrderId(),
        }))
      );

      const combined = [...orders, ...expanded];
      await httpsCallable(functions, 'saveOrders')({ orders: combined });

      const confirm = window.confirm(`${expanded.length} order(s) saved! Clear the form?`);
      if (confirm) { setOrders(combined); setCurrentOrder([]); }
    } catch (e) {
      console.error(e);
      alert('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── Summary stats ─────────────────────────────────────────────────────────
  const totalMealsAll = currentOrder.reduce((s, o) => s + (Number(o.totalMeals) * o.dates.length), 0);
  const totalDates    = currentOrder.reduce((s, o) => s + o.dates.length, 0);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="ao-page">
      <Navbar title="Add Order" />

      <div className="ao-layout">

        {/* ════════════════ LEFT: Builder ════════════════ */}
        <div className="ao-builder">

          {/* Page header */}
          <div className="ao-header">
            <div>
              <h1>Create Order</h1>
              <p>Add meal deliveries for your venues</p>
            </div>
            <button className="ao-add-btn" onClick={handleAdd}>
              <FaPlus /> Add Location Order
            </button>
          </div>

          {/* Empty state */}
          {currentOrder.length === 0 && (
            <div className="ao-empty">
              <div className="ao-empty-icon">
                <FaUtensils />
              </div>
              <h3>No orders yet</h3>
              <p>Click "Add Location Order" to start building your order</p>
              <button className="ao-add-btn-lg" onClick={handleAdd}>
                <FaPlus /> Add your first order
              </button>
            </div>
          )}

          {/* Order cards */}
          {currentOrder.map((order, i) => (
            <div className="ao-card" key={i}>

              {/* Card header */}
              <div className="ao-card-head">
                <div className="ao-card-num">
                  <span>{i + 1}</span>
                </div>
                <span className="ao-card-title">
                  {order.address ? `${order.address}${order.postcode ? ` · ${order.postcode}` : ''}` : `Order #${i + 1}`}
                </span>
                <button className="ao-remove-btn" onClick={() => handleRemove(i)}>
                  <FaTrash />
                </button>
              </div>

              {/* Location row */}
              <div className="ao-section">
                <div className="ao-section-label"><FaMapMarkerAlt /> Location</div>
                <div className="ao-location-row">
                  <div className="ao-field">
                    <label>Postcode</label>
                    <select
                      value={order.postcode}
                      onChange={e => handlePostcodeChange(i, e.target.value)}
                    >
                      <option value="">Select postcode</option>
                      <option value="Pick up">Pick up</option>
                      {venues.map((v, k) => (
                        <option key={k} value={v.postCode}>{v.postCode}</option>
                      ))}
                    </select>
                  </div>
                  <div className="ao-field">
                    <label>Address <small>(auto-fills from postcode)</small></label>
                    <select
                      value={order.address}
                      onChange={e => handleAddressChange(i, e.target.value)}
                    >
                      <option value="">Select address</option>
                      <option value="Pick up">Pick up</option>
                      {venues.map((v, k) => (
                        <option key={k} value={v.address}>{v.address}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Date picker */}
              <div className="ao-section">
                <div className="ao-section-label"><FaCalendarAlt /> Dates <small>(weekdays only · select multiple)</small></div>
                <DatePicker
                  multiple
                  value={order.dates}
                  onChange={sel => handleDatesChange(i, sel)}
                  mapDays={disableWeekends}
                  format="YYYY-MM-DD"
                  placeholder="Click to select dates…"
                  style={{ width: '100%', padding: '0.8rem 1rem', borderRadius: '9px', border: '1.5px solid #D0D5DD', fontSize: '0.9rem', cursor: 'pointer', boxSizing: 'border-box' }}
                  containerStyle={{ width: '100%' }}
                />
                {order.dates.length > 0 && (
                  <div className="ao-date-chips">
                    {order.dates.map(d => (
                      <span key={d} className="ao-date-chip">{d}</span>
                    ))}
                  </div>
                )}
              </div>

              {/* Meal counts */}
              <div className="ao-section">
                <div className="ao-section-label"><FaUtensils /> Meals</div>
                <div className="ao-steppers">
                  <Stepper
                    label="Total meals"
                    sublabel="Min. 15 / day"
                    value={order.totalMeals}
                    onChange={v => handleTotalMeals(i, v)}
                    min={15}
                  />
                  <Stepper
                    label="Special meals"
                    sublabel="Dairy / gluten free"
                    value={order.specialMeals}
                    onChange={v => handleSpecialMeals(i, v)}
                    min={0}
                  />
                </div>
              </div>

              {/* Extras */}
              <div className="ao-section">
                <div className="ao-section-label">Extras <small>(optional)</small></div>
                <div className="ao-extras">
                  <ExtraToggle
                    icon={<FaUtensils />}
                    label="Cutlery"
                    checked={order.cutlery}
                    onToggle={() => updateOrder(i, { cutlery: !order.cutlery })}
                    qty={order.cutleryQuantity}
                    onQty={e => updateOrder(i, { cutleryQuantity: e.target.value })}
                  />
                  <ExtraToggle
                    icon={<FaLeaf />}
                    label="Paper Bowl / Plates"
                    checked={order.paperBowl}
                    onToggle={() => updateOrder(i, { paperBowl: !order.paperBowl })}
                    qty={order.paperBowlQuantity}
                    onQty={e => updateOrder(i, { paperBowlQuantity: e.target.value })}
                  />
                  <ExtraToggle
                    icon={<FaBox />}
                    label="Takeaway Container"
                    checked={order.plasticContainer}
                    onToggle={() => updateOrder(i, { plasticContainer: !order.plasticContainer })}
                    qty={order.plasticContainerQuantity}
                    onQty={e => updateOrder(i, { plasticContainerQuantity: e.target.value })}
                  />
                </div>
              </div>

              {/* Packing instructions */}
              <div className="ao-section">
                <div className="ao-field">
                  <label>Packing instructions <small>(optional)</small></label>
                  <textarea
                    rows={2}
                    placeholder="Any special packing or delivery notes…"
                    value={order.packingInstructions}
                    onChange={e => updateOrder(i, { packingInstructions: e.target.value })}
                  />
                </div>
              </div>

            </div>
          ))}

          {/* Action bar */}
          {currentOrder.length > 0 && (
            <div className="ao-actions">
              <button className="ao-cancel-btn" onClick={() => navigate('/home')}>Cancel</button>
              <button className="ao-save-btn" onClick={handleSave} disabled={saving}>
                <FaSave /> {saving ? 'Saving…' : `Save ${currentOrder.length} Order${currentOrder.length > 1 ? 's' : ''}`}
              </button>
            </div>
          )}
        </div>

        {/* ════════════════ RIGHT: Summary ════════════════ */}
        <aside className="ao-summary">
          <div className="ao-summary-inner">
            <h2 className="ao-summary-title">Order Summary</h2>

            {currentOrder.length === 0 ? (
              <div className="ao-summary-empty">
                <p>Your order summary will appear here as you build it.</p>
              </div>
            ) : (
              <>
                {/* Totals */}
                <div className="ao-summary-stats">
                  <div className="ao-stat">
                    <span className="ao-stat-value">{currentOrder.length}</span>
                    <span className="ao-stat-label">Location{currentOrder.length > 1 ? 's' : ''}</span>
                  </div>
                  <div className="ao-stat">
                    <span className="ao-stat-value">{totalDates}</span>
                    <span className="ao-stat-label">Date{totalDates !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="ao-stat ao-stat-highlight">
                    <span className="ao-stat-value">{totalMealsAll.toLocaleString()}</span>
                    <span className="ao-stat-label">Total meals</span>
                  </div>
                </div>

                {/* Per-order breakdown */}
                <div className="ao-summary-list">
                  {currentOrder.map((o, i) => (
                    <div key={i} className="ao-summary-row">
                      <div className="ao-summary-row-head">
                        <span className="ao-summary-num">{i + 1}</span>
                        <span className="ao-summary-venue">
                          {o.address || 'No location'}
                          {o.postcode ? <em> · {o.postcode}</em> : null}
                        </span>
                      </div>
                      <div className="ao-summary-details">
                        <span>{o.dates.length} date{o.dates.length !== 1 ? 's' : ''}</span>
                        <span className="ao-summary-dot" />
                        <span>{Number(o.totalMeals)} meals / day</span>
                        {o.specialMeals > 0 && (
                          <>
                            <span className="ao-summary-dot" />
                            <span>{o.specialMeals} special</span>
                          </>
                        )}
                      </div>
                      {(o.cutlery || o.paperBowl || o.plasticContainer) && (
                        <div className="ao-summary-extras">
                          {o.cutlery && <span className="ao-extra-chip"><FaUtensils /> Cutlery {o.cutleryQuantity ? `×${o.cutleryQuantity}` : ''}</span>}
                          {o.paperBowl && <span className="ao-extra-chip"><FaLeaf /> Paper bowl {o.paperBowlQuantity ? `×${o.paperBowlQuantity}` : ''}</span>}
                          {o.plasticContainer && <span className="ao-extra-chip"><FaBox /> Takeaway {o.plasticContainerQuantity ? `×${o.plasticContainerQuantity}` : ''}</span>}
                        </div>
                      )}
                      {o.dates.length > 0 && (
                        <div className="ao-summary-dates">
                          {o.dates.map(d => <span key={d} className="ao-mini-chip">{d}</span>)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <button className="ao-save-btn-full" onClick={handleSave} disabled={saving}>
                  <FaSave /> {saving ? 'Saving…' : 'Confirm & Save All'}
                </button>
              </>
            )}
          </div>
        </aside>

      </div>
    </div>
  );
};

export default AddOrder;
